import { useState } from "react";
import { Mail, Instagram, Linkedin, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { SITE } from "@/config";
import { useRevealAnimation, useMagneticButton, usePremiumButton, useTextReveal } from "@/motion/hooks";
import DepthCard from "@/motion/DepthCard";

const channels = [
    { icon: Phone, label: "Call", value: SITE.phone, href: SITE.phoneHref, testId: "contact-phone", external: false },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testId: "contact-email", external: false },
    { icon: Instagram, label: "Instagram", value: SITE.instagramHandle, href: SITE.instagram, testId: "contact-instagram", external: true },
    { icon: Linkedin, label: "LinkedIn", value: "in/thenikhiljoshi", href: SITE.linkedin, testId: "contact-linkedin", external: true },
];

const inputCls =
    "w-full border border-[color:var(--line)] bg-[#060606] px-4 py-3 text-sm text-[color:var(--fg)] placeholder:text-[color:var(--muted)] outline-none transition-colors focus:border-[color:var(--gold)]";

export default function Contact() {
    const revealRef = useRevealAnimation("cinematic", { children: "[data-reveal], [data-testid=contact-whatsapp], form, .grid", stagger: "base" });
    const h2Reveal = useTextReveal("chars");
    const introReveal = useTextReveal("words");
    const waMag = useMagneticButton();
    const waPremium = usePremiumButton("fill");
    const submitPremium = usePremiumButton("fill");
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", message: "" });

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit() {
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setStatus("error");
            return;
        }
        setStatus("sending");
        try {
            const res = await fetch(SITE.formEndpoint, {
                method: "POST",
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data && data.ok) {
                setStatus("sent");
                setForm({ name: "", email: "", phone: "", location: "", message: "" });
            } else {
                throw new Error("bad response");
            }
        } catch (_) {
            setStatus("error");
        }
    }

    return (
        <section ref={revealRef} id="contact" data-testid="contact-section" className="relative py-28 md:py-40">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(198,167,107,0.08),transparent_60%)]" />
            <div className="relative mx-auto max-w-[1000px] px-6 md:px-10 text-center">
                <p className="text-[10px] uppercase tracking-luxe text-[color:var(--gold)]">
                    Let&apos;s create
                </p>
                <h2
                    ref={h2Reveal}
                    className="font-display mt-5 text-6xl md:text-8xl leading-[0.95]"
                >
                    Work With Me
                </h2>
                <p ref={introReveal} className="mx-auto mt-6 max-w-xl font-display italic text-xl text-[color:var(--fg)]/70">
                    For AI brand films, UGC and campaign collaborations — reach out directly.
                </p>

                {/* WhatsApp — primary CTA */}
                <a
                    ref={waPremium}
                    href={SITE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    className="group mt-10 inline-flex items-center gap-3 border border-[color:var(--gold)] bg-[color:var(--gold)]/10 px-8 py-4 text-sm uppercase tracking-[0.22em] text-[color:var(--gold)] transition-colors "
                >
                    <MessageCircle size={18} strokeWidth={1.5} />
                    Chat on WhatsApp
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>

                {/* Contact form -> Google Sheet */}
                <div className="mx-auto mt-14 max-w-[640px] text-left">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <input data-testid="form-name" className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} />
                        <input data-testid="form-email" className={inputCls} placeholder="Email *" type="email" value={form.email} onChange={set("email")} />
                        <input data-testid="form-phone" className={inputCls} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} />
                        <input data-testid="form-location" className={inputCls} placeholder="City / Country" value={form.location} onChange={set("location")} />
                    </div>
                    <textarea
                        data-testid="form-message"
                        className={inputCls + " mt-4 min-h-[130px] resize-y"}
                        placeholder="Tell me about your project *"
                        value={form.message}
                        onChange={set("message")}
                    />
                    <button
                        data-testid="form-submit"
                        ref={submitPremium}
                        onClick={submit}
                        disabled={status === "sending"}
                        className="mt-6 w-full border border-[color:var(--gold)] px-8 py-4 text-sm uppercase tracking-[0.22em] text-[color:var(--gold)] transition-colors  disabled:opacity-50"
                    >
                        {status === "sending" ? "Sending…" : "Send Message"}
                    </button>
                    {status === "sent" && (
                        <p data-testid="form-success" className="mt-4 text-center text-sm text-[color:var(--gold)]">
                            Message sent — I&apos;ll get back to you shortly.
                        </p>
                    )}
                    {status === "error" && (
                        <p data-testid="form-error" className="mt-4 text-center text-sm text-red-400/90">
                            Please fill name, email and message — or if this keeps failing, reach me on WhatsApp or email above.
                        </p>
                    )}
                </div>

                <div className="mt-14 grid grid-cols-1 gap-px border border-[color:var(--line)] bg-[color:var(--line)] sm:grid-cols-2 lg:grid-cols-4">
                    {channels.map((c) => (
                        <DepthCard
                            as="a"
                            maxDeg={3}
                            key={c.label}
                            href={c.href}
                            data-testid={c.testId}
                            {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            className="group flex flex-col items-center gap-3 bg-[#030303] px-6 py-10 transition-colors hover:bg-[color:var(--bg-elev)]"
                        >
                            <c.icon size={22} strokeWidth={1.25} className="text-[color:var(--gold)]" />
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                                {c.label}
                            </span>
                            <span className="flex items-center gap-1 text-sm text-[color:var(--fg)]/85">
                                {c.value}
                                <ArrowUpRight size={13} className="text-[color:var(--muted)] transition-colors group-hover:text-[color:var(--gold)]" />
                            </span>
                        </DepthCard>
                    ))}
                </div>
            </div>
        </section>
    );
}
